import { DateTime } from 'luxon';
import axios from 'axios';
import store from '../reducers/store';
import Amplify, { Auth } from 'aws-amplify';
import amplifyConfig from '../constants/amplify.config';
import { Cookies } from 'react-cookie';

Amplify.configure(amplifyConfig);

// You can get the current config object
const _currentConfig = Auth.configure();

const userService = {
  createUser(email, role) {
    return axios({
      method: 'PUT',
      url: process.env.API_URL + 'users/',
      headers: {
        Authorization: window.localStorage.getItem('access_token'),
        'X-Email': email,
        'X-Org': window.localStorage.getItem('organization_id'),
        'X-Refresh-Token': window.localStorage.getItem('refresh_token'),
        'X-Type': role,
        'X-Username': window.localStorage.getItem('username'),
      },
    })
      .then((response) => {
        return response.data.user;
      })
      .catch((err) => {
        throw JSON.parse(JSON.stringify(err)).response.data;
      });
  },
  editUser(newUser) {
    return axios({
      method: 'PATCH',
      url: process.env.API_URL + 'users/' + newUser.key,
      headers: {
        Authorization: window.localStorage.getItem('access_token'),
        'X-Email': newUser.email,
        'X-Org': window.localStorage.getItem('organization_id'),
        'X-Refresh-Token': window.localStorage.getItem('refresh_token'),
        'X-Role': newUser.role,
        'X-Username': window.localStorage.getItem('username'),
      },
    }).then((response) => {
      return response.data.data;
    });
  },
  deleteUser(id) {
    return axios({
      method: 'DELETE',
      url: process.env.API_URL + 'users/' + id,
      headers: {
        Authorization: window.localStorage.getItem('access_token'),
        'X-Org': window.localStorage.getItem('organization_id'),
        'X-Refresh-Token': window.localStorage.getItem('refresh_token'),
        'X-Username': window.localStorage.getItem('username'),
      },
    }).then((response) => {
      return response.data.data;
    });
  },
  async getUser(cognitoId) {
    const response = await axios({
      method: 'GET',
      url: `${process.env.API_URL}users/${cognitoId}/`,
      headers: {
        Authorization: window.localStorage.getItem('access_token'),
        'X-Org': window.localStorage.getItem('organization_id'),
        'X-Refresh-Token': window.localStorage.getItem('refresh_token'),
        'X-Username': window.localStorage.getItem('username'),
      },
    });

    return response.data.data;
  },
  getUsersFromOrg(orgId) {
    return axios({
      method: 'GET',
      url: process.env.API_URL + 'users/',
      headers: {
        Authorization: window.localStorage.getItem('access_token'),
        'X-Org': window.localStorage.getItem('organization_id'),
        'X-Refresh-Token': window.localStorage.getItem('refresh_token'),
        'X-Username': window.localStorage.getItem('username'),
      },
    })
      .then((response) => {
        store.dispatch({ type: 'UPDATE_SCHOOL_USERS_LIST', schoolUsersList: response.data.users });

        return response.data.users;
      })
      .catch(() => {
        console.log('No users from this school');
      });
  },
  // return org and user id
  async login(username, password) {
    const user = await Auth.signIn(username, password);

    if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
      const error = {
        message: 'A new password is required',
        status: 403,
      };

      throw error;
    }

    window.localStorage.setItem('access_token', JSON.stringify(user.signInUserSession.accessToken));
    window.localStorage.setItem(
      'refresh_token',
      JSON.stringify(user.signInUserSession.refreshToken)
    );

    /**
     * Fetch the user and org
     */
    const userInfo = await this.getUser(user.signInUserSession.accessToken.payload.sub);

    this.resetOrgThroughHeaders(JSON.stringify(userInfo.organization));

    window.localStorage.setItem('username', userInfo.username);
    window.localStorage.setItem('user_id', userInfo.id);

    this.loggedIn = true;

    userInfo.status = 200;

    return userInfo;
  },
  resetOrgThroughHeaders(org) {
    const fullOrg = JSON.parse(org);

    window.localStorage.setItem(
      'hasRivalsCampData',
      fullOrg.package.has_rivals_camp === true ? 'true' : 'false'
    );
    window.localStorage.setItem(
      'isRivalsOnly',
      fullOrg.package.is_rivals_only === true ? 'true' : 'false'
    );
    window.localStorage.setItem('isZcruitLite', fullOrg.package.is_lite ? 'true' : 'false');
    window.localStorage.setItem('isZmailOnly', fullOrg.package.is_zmail);
    window.localStorage.setItem('organization_id', fullOrg.id);
    window.localStorage.setItem('organization', org);
    window.localStorage.setItem('school', fullOrg.name);

    /**
     * Set a cookie to communicate between zmail/zcruit
     */
    const cookies = new Cookies();

    const temp = JSON.parse(window.localStorage.getItem('organization'));

    if (typeof temp.customTypes !== 'undefined') {
      delete temp.customTypes;
    }

    delete temp.package.packageSources;

    cookies.set('organization', JSON.stringify(temp), {
      domain: process.env.ZCRUIT_COOKIE_DOMAIN,
      expires: DateTime.now().plus({ days: 30 }).toJSDate(),
      path: '/',
    });

    store.dispatch({
      type: 'ORGANIZATION_CHANGED',
    });
  },
  async resetPasswordForNewUser(username, password, newPassword) {
    const userOld = await Auth.signIn(username, password);

    const user = await Auth.completeNewPassword(userOld, newPassword, []);

    window.localStorage.setItem('access_token', JSON.stringify(user.signInUserSession.accessToken));
    window.localStorage.setItem(
      'refresh_token',
      JSON.stringify(user.signInUserSession.refreshToken)
    );

    this.loggedIn = true;

    return user;
  },
  async newPassword(username, code, password) {
    await Auth.forgotPasswordSubmit(username, code, password);

    await this.login(username, password);

    this.loggedIn = true;
  },
  async logout() {
    const logout = await Auth.signOut();

    window.localStorage.removeItem('access_token');
    window.localStorage.removeItem('idi');
    window.localStorage.removeItem('hasRivalsCampData');
    window.localStorage.removeItem('isRivalsOnly');
    window.localStorage.removeItem('isZmailOnly');
    window.localStorage.removeItem('isZcruitLite');
    window.localStorage.removeItem('access_token');
    window.localStorage.removeItem('school');
    window.localStorage.removeItem('organization_id');
    window.localStorage.removeItem('organization');
    window.localStorage.removeItem('refresh_token');
    window.localStorage.removeItem('username');
    window.localStorage.removeItem('user_id');

    const cookies = new Cookies();

    cookies.remove('organization', {
      domain: process.env.ZCRUIT_COOKIE_DOMAIN,
      expires: 0,
      path: '/',
    });

    return true;
  },
  async isLoggedIn() {
    let loggedIn = true;

    let user = null;
    try {
      user = await Auth.currentAuthenticatedUser();
    } catch (e) {
      await this.logout();

      return false;
    }

    /**
     * Ensures we reset the auth_token fully, as it will be auto-refreshed after an hour
     */
    const accessToken = JSON.stringify(user.signInUserSession.accessToken);
    const refreshToken = JSON.stringify(user.signInUserSession.refreshToken);

    window.localStorage.setItem('access_token', accessToken);
    window.localStorage.setItem('refresh_token', refreshToken);

    const cookies = new Cookies();
    const org = cookies.get('organization');

    /**
     * Always get the user to ensure we got the right one. If there is no cookie, assume logged out
     */
    if (typeof org === 'undefined' || org === null || org === '') {
      loggedIn = false;

      await this.logout();
    } else {
      window.localStorage.setItem('organization_id', org.id);
      window.localStorage.setItem('organization', JSON.stringify(org));

      user = await this.getUser(user.signInUserSession.accessToken.payload.sub);

      this.resetOrgThroughHeaders(JSON.stringify(user.organization));
      window.localStorage.setItem('user_id', user.id);
    }

    window.localStorage.setItem('username', user.username);

    this.loggedIn = loggedIn;

    return this.loggedIn;
  },
};

// export default UserService

export default userService;



// WEBPACK FOOTER //
// ./src/services/userService.js